import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@socialflows/shared-lib';

export interface PageSidebarItem {
  label: string;
  url: string;
  icon?: LucideIcon;
  badge?: string | number;
  onClick?: () => void;
}

export interface PageSidebarSection {
  title?: string;
  items: PageSidebarItem[];
}

interface PageSidebarProps {
  sections: PageSidebarSection[];
  className?: string;
}

export function PageSidebar({ sections, className }: PageSidebarProps) {
  const pathname = useLocation().pathname;

  return (
    <aside className={cn('w-60 border-r bg-muted/10 flex-shrink-0 overflow-y-auto', className)}>
      <div className="p-3 space-y-4">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {section.title && (
              <h3 className="mb-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <nav className="space-y-0.5">
              {section.items.map((item, itemIndex) => {
                const Icon = item.icon;
                const isActive =
                  pathname === item.url || (item.url !== '#' && pathname.startsWith(item.url));

                const content = (
                  <>
                    {Icon && <Icon className="h-4 w-4 mr-3 shrink-0" />}
                    <span className="flex-1 truncate text-sm">{item.label}</span>
                    {item.badge !== undefined && (
                      <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </>
                );

                const baseClasses = cn(
                  'w-full justify-start h-9 px-3 transition-colors',
                  isActive
                    ? 'bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80'
                    : 'hover:bg-secondary/50'
                );

                if (item.onClick) {
                  return (
                    <Button
                      key={itemIndex}
                      variant="ghost"
                      className={baseClasses}
                      onClick={item.onClick}
                    >
                      {content}
                    </Button>
                  );
                }

                return (
                  <Button key={itemIndex} variant="ghost" className={baseClasses} asChild>
                    <Link to={item.url}>{content}</Link>
                  </Button>
                );
              })}
            </nav>
          </div>
        ))}
      </div>
    </aside>
  );
}
